<script lang="ts" setup>
	const props = defineProps<{
		metaDetails: PolicyResponse | null
		premium?: string
		isPaused?: boolean
		isDeactivated?: boolean
		isPending?: boolean
	}>()

	const { downloadFile } = useFileExplorer()
	const { getStatus } = useUtils()
	const { formatDate, getDateDifference } = useCustomDate()

	defineEmits<{
		(event: 'activate'): void
		(event: 'reminder'): void
	}>()
	const isHmoOpen = shallowRef(false)
	const downloadingHmo = shallowRef(false)
	const {
		renewalDate,
		activationDate,
		purchaseDate,
		policyNumber,
		dateAdded,
		totalNoOfDependant,
		hmoId,
		hmoCard,
		policyAuthor,
		canAccessHmo,
		isPolicyPending,
		activationFailed
	} = usePolicyInfo(() => ({
		policyInfo: props.metaDetails || null
	}))
	const policyDetails = computed(() => ({
		'Purchase Date': formatDate(purchaseDate.value, 'DD MMM, YYYY'),
		'Activation Date': activationDate.value.formatted,
		'Expiry Date': renewalDate.value.formatted,
		'Date Added': formatDate(dateAdded.value, 'DD MMM, YYYY'),
		'H.M.O ID': policyNumber.value,
		'No. of Dependants': totalNoOfDependant.value
	}))
	const planStatus = computed(() => {
		return getStatus(props.metaDetails)
	})
	const metaLabel = computed(() => {
		let title = 'No Plan Detail'
		let subTitle = 'This staff cannot access care at the hospital till they activate their plan'
		if (planStatus.value?.to_be_removed) {
			title = 'Awaiting Plan Deactivation'
			subTitle = 'This staff Plan is Awaiting Deactivation'
		}
		if (planStatus.value?.is_deactivated) {
			title = 'Plan Deactivated'
			subTitle = 'This staff cannot access care because their plan has been deactivated'
		}
		if (props.isPaused && !planStatus.value?.to_be_removed) {
			title = 'Plan Paused'
			subTitle =
				'This plan is “Paused” as the staff assigned has rejected your plan. This staff can be removed from your plan.'
		}
		if (!activationDate.value.raw) {
			title = 'Plan failed to activate'
			subTitle = 'Something went wrong with this staff policy. <br /> Kindly reach out to support.'
		}
		return {
			title,
			subTitle
		}
	})
	const viewPolicy = (downloadable = false) => {
		if (!hmoCard.value) {
			return useNotification().alert({
				status: 400,
				message: 'HMO ID Not available'
			})
		}
		if (!downloadable) {
			isHmoOpen.value = !isHmoOpen.value
			return null
		}
		downloadingHmo.value = true
		downloadFile(hmoCard.value, `${policyAuthor.value.full_name} hmo.png`).finally(() => {
			downloadingHmo.value = false
		})
	}
</script>

<template>
	<div>
		<my-cover-pending-policy-placeholder
			icon-dir="documents"
			:title="metaLabel.title"
			:sub-title="metaLabel.subTitle"
			icon-name="clipboard-document-2"
			:is-pending="isPolicyPending || !activationDate.raw"
			activate-text="Activate Staff"
			:use-btn="!isDeactivated"
			@activate="$emit('activate')"
			@reminder="$emit('reminder')"
		>
			<template #button-group>
				<div></div>
			</template>
		</my-cover-pending-policy-placeholder>
		<my-cover-health-waiting-period-banner
			v-if="!canAccessHmo && !isPolicyPending && !activationFailed"
			:date="dateAdded"
		/>
		<my-cover-policy-card
			v-else-if="!isPolicyPending && activationDate.raw"
			:details="policyDetails"
			:use-footer="!!hmoCard || isPolicyPending"
			:downloading="downloadingHmo"
			:pending-id="isPolicyPending && !hmoCard"
			@view="viewPolicy()"
			@download="viewPolicy(true)"
		>
			<template #value="{ record, dataIndex }">
				<template v-if="dataIndex === 'policy_certificate'">
					<div
						class="flex min-h-[44px] w-full items-center gap-x-3 rounded-md border border-gray-100 bg-gray-50 p-3"
					>
						<lazy-tools-icon dir="documents" icon="document-text" path-class="fill-gray-300" />
						<span>
							{{ record || '-------' }}
						</span>
					</div>
				</template>
			</template>
		</my-cover-policy-card>

		<a-image
			:width="200"
			:style="{ display: 'none' }"
			:preview="{
				visible: isHmoOpen,
				onVisibleChange: viewPolicy
			}"
			:src="hmoCard"
			loading="lazy"
		/>
	</div>
</template>
